import React, { useMemo } from 'react';
import {
  List,
  ListItem,
  ListItemButton,
  ListItemAvatar,
  ListItemText,
  Typography,
  Box,
  Chip,
  Badge,
  Tabs,
  Tab,
  Button,
  Fab,
} from '@mui/material';
import PersonAddIcon from '@mui/icons-material/PersonAdd';
import UserAvatar from './UserAvatar';
import GroupList from './GroupList';
import FoldersTree from './FoldersTree';
import { stripHtmlTags } from '../utils/textUtils';

function getDisplayTeams(user, currentUser) {
  if (user.sharedTeams?.length > 0) {
    return user.sharedTeams;
  }
  if (user.teams?.length > 0) {
    return user.teams;
  }
  const currentTeams = currentUser?.teams || [];
  const userTeamIds = user.teamIds || [];
  return currentTeams
    .filter((t) => userTeamIds.includes(t.id))
    .map((t) => ({ id: t.id, name: t.name }));
}

function isDirectContact(user, currentUserId, currentUserContactIds) {
  const userContactIds = user.contactIds || [];
  return (
    currentUserContactIds.includes(user.id) ||
    userContactIds.includes(currentUserId)
  );
}

function UserTeamChips({ teams }) {
  if (!teams?.length) return null;
  return (
    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mb: 0.25 }}>
      {teams.map((team) => (
        <Chip
          key={team.id}
          label={team.name}
          size="small"
          color="primary"
          variant="outlined"
          sx={{
            height: 22,
            fontSize: '0.72rem',
            maxWidth: '100%',
            '& .MuiChip-label': {
              overflow: 'hidden',
              textOverflow: 'ellipsis',
            },
          }}
        />
      ))}
    </Box>
  );
}

function UserList({ 
  users, 
  messages, 
  currentUserId, 
  selectedUserId, 
  onSelectUser, 
  lastReadTimestamps, 
  typingUsers,
  groups,
  selectedGroupId,
  onSelectGroup,
  onCreateGroupClick,
  onInviteUserClick,
  currentUser,
  activeTab = 0,
  onTabChange,
  folders = [],
  foldersLoading = false,
  selectedFolderId = null,
  onSelectFolder,
  onCreateFolder,
  onRenameFolder,
  onMoveFolder,
  onDeleteFolder,
  folderViewSource = 'mine',
  onFolderViewChange,
  sharedContacts = [],
  onUpdateFolderSharing,
  onRefreshFolders,
  onFilesMoved,
}) {
  const handleTabChange = (event, newValue) => {
    onTabChange?.(newValue);
    // Clear chat selection when moving off the users/groups tabs
    if (newValue === 0) {
      onSelectGroup(null);
    } else if (newValue === 1) {
      onSelectUser(null);
    } else {
      onSelectUser(null);
      onSelectGroup(null);
    }
  };
  // Sort users: online first, then by last message time
  // Filter by shared teams or direct contacts
  const sortedUsers = useMemo(() => {
    const currentUserTeamIds = currentUser?.teamIds || [];
    const currentUserContactIds = currentUser?.contactIds || [];
    
    let filteredUsers = users;
    if (currentUserTeamIds.length === 0 && currentUserContactIds.length === 0) {
      filteredUsers = [];
    } else {
      filteredUsers = users.filter(user => {
        const userTeamIds = user.teamIds || [];
        const userContactIds = user.contactIds || [];
        if (currentUserContactIds.includes(user.id) || userContactIds.includes(currentUserId)) {
          return true;
        }
        if (currentUserTeamIds.length > 0 && userTeamIds.length > 0) {
          return userTeamIds.some(teamId => currentUserTeamIds.includes(teamId));
        }
        return false;
      });
    }
    
    const usersWithLastMessage = filteredUsers.map(user => {
      if (user.id === currentUserId) return null; // Don't show current user

      // Find last message in conversation with this user
      const conversationId = [currentUserId, user.id].sort().join('::');
      const userMessages = messages.filter(msg => 
        msg.conversationId === conversationId && msg.senderId !== currentUserId
      );
      const lastMessage = userMessages.length > 0 
        ? userMessages[userMessages.length - 1]
        : null;

      // Calculate unread count
      const lastReadTimestamp = lastReadTimestamps[conversationId];
      const unreadCount = lastReadTimestamp
        ? userMessages.filter(msg => new Date(msg.timestamp) > new Date(lastReadTimestamp)).length
        : userMessages.length;

      return {
        ...user,
        lastMessage,
        lastMessageTime: lastMessage ? new Date(lastMessage.timestamp) : new Date(0),
        unreadCount,
      };
    }).filter(Boolean);

    return usersWithLastMessage.sort((a, b) => {
      // Online users first
      if (a.online !== b.online) {
        return b.online ? 1 : -1;
      }
      // Then by last message time
      return b.lastMessageTime - a.lastMessageTime;
    });
  }, [users, messages, currentUserId, lastReadTimestamps, currentUser]);

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: 'calc(100vh - 64px)', overflow: 'hidden' }}>
      <Tabs value={activeTab} onChange={handleTabChange} variant="fullWidth">
        <Tab label="Users" />
        <Tab label="Groups" />
        <Tab label="Files" />
      </Tabs>
      
      <Box sx={{ flex: 1, overflow: 'hidden', display: 'flex', flexDirection: 'column', minHeight: 0 }}>
        {activeTab === 0 ? (
          <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%', overflow: 'hidden' }}>
            <Box sx={{ flex: 1, overflow: 'auto', minHeight: 0 }}>
              <List sx={{ width: '100%', bgcolor: 'background.paper' }}>
                {sortedUsers.length === 0 ? (
                  <Box sx={{ p: 2, textAlign: 'center' }}>
                    <Typography variant="body2" color="text.secondary">
                      No other users yet
                    </Typography>
                  </Box>
                ) : (
                sortedUsers.map((user) => {
                  const displayTeams = getDisplayTeams(user, currentUser);
                  const directOnly =
                    displayTeams.length === 0 &&
                    isDirectContact(
                      user,
                      currentUserId,
                      currentUser?.contactIds || []
                    );

                  return (
                  <ListItem key={user.id} disablePadding>
                    <ListItemButton
                      selected={selectedUserId === user.id}
                      onClick={() => onSelectUser(user.id)}
                    >
                      <ListItemAvatar>
                        <Badge
                          badgeContent={user.unreadCount > 0 ? user.unreadCount : 0}
                          color="primary"
                          overlap="circular"
                          anchorOrigin={{
                            vertical: 'bottom',
                            horizontal: 'right',
                          }}
                          sx={{
                            '& .MuiBadge-badge': {
                              minWidth: '20px',
                              height: '20px',
                              borderRadius: '10px',
                              fontSize: '0.75rem',
                            },
                          }}
                        >
                          <UserAvatar name={user.username} online={user.online} away={user.away} profilePictureUrl={user.profilePictureUrl} />
                        </Badge>
                      </ListItemAvatar>
                      <ListItemText
                        primary={
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <Typography variant="body1" sx={{ fontWeight: 500 }}>
                              {user.username}
                            </Typography>
                            {user.online && !user.away && (
                              <Chip
                                label="Online"
                                size="small"
                                color="success"
                                sx={{ height: 20, fontSize: '0.7rem' }}
                              />
                            )}
                            {user.online && user.away && (
                              <Chip
                                label="Away"
                                size="small"
                                color="warning"
                                sx={{ height: 20, fontSize: '0.7rem' }}
                              />
                            )}
                          </Box>
                        }
                        secondary={
                          <Box component="span" sx={{ display: 'block' }}>
                            <UserTeamChips teams={displayTeams} />
                            {directOnly && (
                              <Chip
                                label="Direct contact"
                                size="small"
                                variant="outlined"
                                sx={{ height: 22, fontSize: '0.72rem', mb: 0.25 }}
                              />
                            )}
                            {typingUsers && typingUsers.has(user.id) ? (
                              <Typography
                                variant="body2"
                                color="primary"
                                component="span"
                                sx={{
                                  display: 'block',
                                  overflow: 'hidden',
                                  textOverflow: 'ellipsis',
                                  whiteSpace: 'nowrap',
                                  fontStyle: 'italic',
                                }}
                              >
                                {user.username} is typing...
                              </Typography>
                            ) : user.lastMessage ? (
                              <Typography
                                variant="body2"
                                color="text.secondary"
                                component="span"
                                sx={{
                                  display: 'block',
                                  overflow: 'hidden',
                                  textOverflow: 'ellipsis',
                                  whiteSpace: 'nowrap',
                                }}
                              >
                                {stripHtmlTags(user.lastMessage.message)}
                              </Typography>
                            ) : null}
                          </Box>
                        }
                        secondaryTypographyProps={{ component: 'div' }}
                      />
                    </ListItemButton>
                  </ListItem>
                  );
                })
              )}
              </List>
            </Box>
            {onInviteUserClick && (
              <Box sx={{ p: 2, bgcolor: 'background.paper', borderTop: '1px solid', borderColor: 'divider', flexShrink: 0 }}>
                <Button
                  fullWidth
                  variant="contained"
                  startIcon={<PersonAddIcon />}
                  onClick={onInviteUserClick}
                >
                  Invite User
                </Button>
              </Box>
            )}
          </Box>
        ) : activeTab === 1 ? (
          <GroupList
            groups={groups}
            messages={messages}
            currentUserId={currentUserId}
            selectedGroupId={selectedGroupId}
            onSelectGroup={onSelectGroup}
            lastReadTimestamps={lastReadTimestamps}
            onCreateGroupClick={onCreateGroupClick}
          />
        ) : (
          <FoldersTree
            folders={folders}
            loading={foldersLoading}
            selectedFolderId={selectedFolderId}
            onSelectFolder={onSelectFolder}
            onCreateFolder={onCreateFolder}
            onRenameFolder={onRenameFolder}
            onMoveFolder={onMoveFolder}
            onDeleteFolder={onDeleteFolder}
            users={users}
            currentUserId={currentUserId}
            currentUser={currentUser}
            folderViewSource={folderViewSource}
            onFolderViewChange={onFolderViewChange}
            sharedContacts={sharedContacts}
            onUpdateFolderSharing={onUpdateFolderSharing}
            onRefreshFolders={onRefreshFolders}
            onFilesMoved={onFilesMoved}
          />
        )}
      </Box>
    </Box>
  );
}

export default UserList;

